<template>
  <div class="unsubscribe-page">
    <div class="unsubscribe-card">
      <!-- Loading State -->
      <div v-if="state === 'loading'" class="state-container">
        <div class="spinner"></div>
        <p class="state-text">Validating your request...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="state === 'error'" class="state-container">
        <div class="state-icon error-icon">✕</div>
        <h2 class="state-title">Invalid Link</h2>
        <p class="state-text">{{ errorMessage }}</p>
      </div>

      <!-- Confirm Unsubscribe -->
      <div v-else-if="state === 'confirm'" class="state-container">
        <div class="state-icon warning-icon">
          <i class="fas fa-envelope-open-text"></i>
        </div>
        <h2 class="state-title">Unsubscribe from Newsletter</h2>
        <p class="state-text">
          Hi <strong>{{ memberInfo.member_name }}</strong>,<br>
          Are you sure you want to unsubscribe <strong>{{ memberInfo.email }}</strong> from our newsletter?
        </p>

        <div class="reason-section">
          <label class="reason-label">Tell us why (optional):</label>
          <select v-model="selectedReason" class="reason-select">
            <option value="">Select a reason</option>
            <option value="too_many_emails">Too many emails</option>
            <option value="not_relevant">Content is not relevant</option>
            <option value="never_subscribed">I never subscribed</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div class="action-buttons">
          <button 
            class="btn-unsubscribe" 
            @click="submitUnsubscribe" 
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="btn-spinner"></span>
            {{ isSubmitting ? 'Processing...' : 'Unsubscribe' }}
          </button>
          <button class="btn-cancel" @click="goHome">
            Keep Subscribed
          </button>
        </div>
      </div>

      <!-- Already Unsubscribed -->
      <div v-else-if="state === 'already_unsubscribed'" class="state-container">
        <div class="state-icon info-icon">ℹ</div>
        <h2 class="state-title">Already Unsubscribed</h2>
        <p class="state-text">
          <strong>{{ memberInfo.member_name }}</strong>, you have already unsubscribed from our newsletter.
        </p>
        <button class="btn-resubscribe" @click="submitResubscribe">
          Re-subscribe
        </button>
      </div>

      <!-- Success: Unsubscribed -->
      <div v-else-if="state === 'success'" class="state-container">
        <div class="state-icon success-icon">✓</div>
        <h2 class="state-title">Successfully Unsubscribed</h2>
        <p class="state-text">
          You will no longer receive newsletter emails from us.
        </p>
        <button class="btn-resubscribe" @click="submitResubscribe">
          Changed your mind? Re-subscribe
        </button>
      </div>

      <!-- Success: Resubscribed -->
      <div v-else-if="state === 'resubscribed'" class="state-container">
        <div class="state-icon success-icon">✓</div>
        <h2 class="state-title">Welcome Back!</h2>
        <p class="state-text">
          You have been re-subscribed to our newsletter. You'll receive our latest updates again.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/helpers/api';

export default {
  name: 'NewsletterUnsubscribe',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const state = ref('loading');
    const errorMessage = ref('');
    const memberInfo = ref({});
    const selectedReason = ref('');
    const isSubmitting = ref(false);
    const token = ref('');

    onMounted(async () => {
      token.value = route.query.token;

      if (!token.value) {
        state.value = 'error';
        errorMessage.value = 'No token provided. Please use the link from your email.';
        return;
      }

      try {
        const response = await api.request('get', `/api/newsletters/unsubscribe`, {
          custom: { token: token.value }
        });

        const data = response.data.data;
        memberInfo.value = data;

        if (!data.is_subscribed) {
          state.value = 'already_unsubscribed';
        } else {
          state.value = 'confirm';
        }
      } catch (error) {
        state.value = 'error';
        errorMessage.value = error.response?.data?.message || 'Invalid or expired unsubscribe link.';
      }
    });

    async function submitUnsubscribe() {
      isSubmitting.value = true;
      try {
        await api.request('post', '/api/newsletters/unsubscribe', {}, {
          token: token.value,
          reason: selectedReason.value || null,
        });
        state.value = 'success';
      } catch (error) {
        errorMessage.value = error.response?.data?.message || 'Failed to unsubscribe. Please try again.';
        state.value = 'error';
      } finally {
        isSubmitting.value = false;
      }
    }

    async function submitResubscribe() {
      isSubmitting.value = true;
      try {
        await api.request('post', '/api/newsletters/resubscribe', {}, {
          token: token.value,
        });
        state.value = 'resubscribed';
      } catch (error) {
        errorMessage.value = error.response?.data?.message || 'Failed to re-subscribe. Please try again.';
        state.value = 'error';
      } finally {
        isSubmitting.value = false;
      }
    }

    function goHome() {
      router.push('/');
    }

    return {
      state,
      errorMessage,
      memberInfo,
      selectedReason,
      isSubmitting,
      submitUnsubscribe,
      submitResubscribe,
      goHome,
    };
  },
};
</script>

<style scoped>
.unsubscribe-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f6fa;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.unsubscribe-card {
  background: white;
  border-radius: 16px;
  padding: 48px 40px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 20px;
}

.icon-img {
  width: 32px;
  height: 32px;
}

.success-icon {
  background: #e8f5e9;
  color: #2e7d32;
}

.error-icon {
  background: #ffebee;
  color: #c62828;
}

.warning-icon {
  background: #fff3e0;
  color: #e65100;
}

.info-icon {
  background: #e3f2fd;
  color: #1565c0;
}

.state-title {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 12px;
}

.state-text {
  font-size: 15px;
  line-height: 1.6;
  color: #64748b;
  margin: 0 0 24px;
}

.reason-section {
  width: 100%;
  margin-bottom: 24px;
}

.reason-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 8px;
  text-align: left;
}

.reason-select {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #334155;
  background: white;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
}

.reason-select:focus {
  border-color: var(--primary-color, #F93E3E);
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.btn-unsubscribe {
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: #dc2626;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-unsubscribe:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-unsubscribe:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  width: 100%;
  padding: 12px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  color: #64748b;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-resubscribe {
  padding: 12px 28px;
  border: 1px solid var(--primary-color, #F93E3E);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
  color: var(--primary-color, #F93E3E);
}

.btn-resubscribe:hover {
  background: #fff5f5;
}

/* Spinner */
.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: var(--primary-color, #F93E3E);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 480px) {
  .unsubscribe-card {
    padding: 32px 24px;
  }

  .state-title {
    font-size: 20px;
  }
}
</style>
